import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Instagram, Facebook } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { contactInfo } from '../mock';
import Logo from './Logo';

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-black border-t border-white/10 text-white py-16 mt-24">
      <div className="container mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <Logo width="220" className="mb-4" />
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t({ sl: 'Hitre povezave', en: 'Quick Links', it: 'Link Rapidi' })}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/services" className="text-gray-400 hover:text-white transition-colors body-small">
                  {t({ sl: 'Storitve', en: 'Services', it: 'Servizi' })}
                </Link>
              </li>
              <li>
                <Link to="/portfolio" className="text-gray-400 hover:text-white transition-colors body-small">
                  {t({ sl: 'Naši projekti', en: 'Our Projects', it: 'I Nostri Progetti' })}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors body-small">
                  {t({ sl: 'O nas', en: 'About Us', it: 'Chi Siamo' })}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors body-small">
                  {t({ sl: 'Kontakt', en: 'Contact', it: 'Contatti' })}
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t({ sl: 'Ostanite v stiku z nami', en: 'Stay Connected', it: 'Resta in Contatto' })}
            </h4>
            <div className="flex flex-col space-y-3">
              <a 
                href="https://www.instagram.com/matrixprint.si/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-400 hover:text-pink-500 transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-pink-500/20 transition-colors">
                  <Instagram className="w-5 h-5" />
                </div>
                <span className="body-small">@matrixprint.si</span>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61595280029305"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-400 hover:text-blue-500 transition-colors group"
                data-testid="footer-facebook-link"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                  <Facebook className="w-5 h-5" />
                </div>
                <span className="body-small">Matrix Print</span>
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t({ sl: 'Kontakt', en: 'Contact', it: 'Contatti' })}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                <span className="text-gray-400 body-small">{t(contactInfo.address)}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-magenta-400" />
                <a href={`tel:${contactInfo.phone}`} className="text-gray-400 hover:text-white body-small transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-yellow-400" />
                <a href={`mailto:${contactInfo.email}`} className="text-gray-400 hover:text-white body-small transition-colors">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                <span className="text-gray-400 body-small">{t(contactInfo.hours)}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 body-small">
            © {new Date().getFullYear()} Matrix Print. {t({ sl: 'Vse pravice pridržane.', en: 'All rights reserved.', it: 'Tutti i diritti riservati.' })}
          </p>
          <Link to="/privacy-policy" className="text-gray-400 body-small hover:text-white transition-colors">
            {t({ sl: 'Politika zasebnosti', en: 'Privacy Policy', it: 'Informativa sulla Privacy' })}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
